import { useState } from 'react';
import type { RoleType } from '@isg/shared-types';
import { Anchor, Button, Card, Group, Select, Stack, Text, TextInput, Title } from '@mantine/core';
import { Link } from 'react-router';
import type { AppUserSummary } from '@isg/shared-types';
import { useAppUsers, useCiktiIndir, usePersons, useKapilar } from '../api/queries';
import { SAPKA_ETIKET } from '../i18n/tr';
import { Rozet, Sayfalama, SuzgecGostergesi, KapiSebebi } from '../sozlesme';
import { BosListeNotu, QueryStatus } from './parts';
import { HesapBagEylemleri } from './calisan-parts';
import { SapkaBahceDuzenleyici, bahcesiBosMu } from './yetki-parts';

/**
 * KULLANICILAR — **İKİNCİ EKRAN, AYNI KAPI** (F4·B2, karar **Y1**:
 * Kapsam §20.7; IA §4'ün 14. satırı — *"hesap + şapka atama"*).
 *
 * > **Kişi kartı ASIL yüzeydir; bu ekran aynı uçları çağırır.**
 *
 * **İKİ İŞİ VAR ve ikincisi asıl gerekçedir:**
 *
 * 1. **Atama** — şapka ekseninde toplu bakış; yazma `yetki-parts.tsx`ten
 *    (tek bileşen, tek yol). ⚠️ Buraya kendi mutasyonunu yazan kod Y1'i
 *    ihlal eder.
 * 2. **DENETİM** — *"bahçesi boş şapka var mı"* ve *"kaç holding yöneticisi
 *    var"*. **Bu iki soruyu kişi kartı YAPISAL OLARAK cevaplayamaz:**
 *    fail-closed kural gereği bahçesi boş şapka **hiçbir şey görmez** ve bu
 *    tek tek kartlara bakarken **görünmez** — ancak şapka ekseninde,
 *    liste hâlinde belirir.
 *
 * **Sözleşme (P4):** liste ekranıdır → **sayfalama + GÖRÜNÜR süzgeç.**
 * ⚠️ Süzgeç istemcide çalışır ve bu bilinçlidir: kullanıcı sayısı bir
 * tenant'ta yüzler mertebesindedir; **sunucuya süzgeç parametresi eklemek**
 * aynı kuralı iki dile yazmak olurdu. Toplam sayı yine **görünür** — çıktı
 * motorunun *"şu anki süzgeçle N kayıt"* cümlesi bozulmaz.
 *
 * **Yetki: YENİ EKSEN YOK.** Ekran `GET /api/app-users`i okur (holding +
 * şirket yöneticisi); şapka verme yine yalnız holding yöneticisindedir ve
 * karar **guard'dadır** — burası yalnız görünümü sadeleştirir (§2.16).
 */

const SAYFA_BOYUTU = 20;

type DurumSuzgeci = 'hepsi' | 'bos_bahce' | 'sapkasiz' | 'hic_girmedi' | 'bekleyen';

const DURUM_SECENEKLERI: { value: DurumSuzgeci; label: string }[] = [
  { value: 'hepsi', label: 'Tümü' },
  // DENETİM süzgeci — ekranın ikinci işi (fail-closed'un görünür kılınması).
  { value: 'bos_bahce', label: 'Bahçesi boş şapka' },
  { value: 'sapkasiz', label: 'Şapkası yok (çalışan)' },
  { value: 'hic_girmedi', label: 'Hiç giriş yapmadı' },
  // ✏️ **TUR 9 / B3 (T9-21) — BEKLEYEN HESAP KUYRUĞU.** Zil *"N hesap
  // bekliyor"* diyordu ve gidilecek bir ekran YOKTU (§2.40'ın *"uyarı var,
  // çare yok"* kolu). Kuyruk ayrı bir liste değil, bu listenin bir
  // SÜZGECİDİR: aynı satır, aynı kart, aynı yazma yolu.
  { value: 'bekleyen', label: 'Kişiye bağlanmayı bekleyen hesaplar' },
];

export function KullanicilarScreen(): React.JSX.Element {
  const kullanicilar = useAppUsers();
  // P13-b (madde 225): şapka verme kapısı SUNUCUDAN (`SAPKA_VERME_SAPKALARI` — §20.5/A4).
  const kapilar = useKapilar();
  const yazabilir = kapilar.data?.sapkaVermeKapisi.yazabilir ?? false;
  // Bağ kurma/çözme yalnız holding yöneticisinde (§20.5/A4 — karar guard'da).
  const bagYazabilir = yazabilir;
  const indir = useCiktiIndir();
  // Bağ kurulacak kişi havuzu — bahçe filtreli liste ucundan (ikinci havuz yok).
  const kisiler = usePersons('hepsi');
  const kisiSecenekleri = (kisiler.data ?? []).map((k) => ({ value: k.id, label: k.adSoyad }));

  const [arama, setArama] = useState('');
  const [sapkaSuzgeci, setSapkaSuzgeci] = useState<string | null>(null);
  const [durum, setDurum] = useState<DurumSuzgeci>('hepsi');
  const [sayfa, setSayfa] = useState(1);

  const hepsi = kullanicilar.data ?? [];
  const aranan = arama.trim().toLocaleLowerCase('tr');
  const suzulmus = hepsi.filter((kullanici) => {
    // Ad VEYA e-posta: kişiye bağlı olmayan hesabın (denetçi/danışman) tek
    // tanınır alanı e-postasıdır.
    if (
      aranan &&
      !`${kullanici.adSoyad ?? ''} ${kullanici.email ?? ''}`
        .toLocaleLowerCase('tr')
        .includes(aranan)
    ) {
      return false;
    }
    if (sapkaSuzgeci && !kullanici.atamalar.some((a) => a.sapka === sapkaSuzgeci)) return false;
    if (durum === 'bos_bahce') return kullanici.atamalar.some(bahcesiBosMu);
    if (durum === 'sapkasiz') return kullanici.atamalar.length === 0;
    if (durum === 'hic_girmedi') return kullanici.hicGirmedi;
    if (durum === 'bekleyen') return kullanici.personId === null;
    return true;
  });
  const etkinSuzgec = (aranan ? 1 : 0) + (sapkaSuzgeci ? 1 : 0) + (durum === 'hepsi' ? 0 : 1);
  const sayfadakiler = suzulmus.slice((sayfa - 1) * SAYFA_BOYUTU, sayfa * SAYFA_BOYUTU);

  const bekleyenSayisi = hepsi.filter((k) => k.personId === null).length;

  return (
    <Stack>
      <Group justify="space-between" align="flex-end">
        <Title order={3}>Kullanıcılar</Title>
        {kapilar.data && (
          <KapiSebebi
            durum={kapilar.data.sapkaVermeKapisi}
            nesne="Şapkayı"
            fiil="verir"
            testId="sapka-verme-yazamama-sebebi"
          />
        )}
        <Group gap="sm" align="flex-end">
          {bekleyenSayisi > 0 && (
            <Button
              variant="default"
              data-testid="bekleyen-hesap-kisayolu"
              onClick={() => {
                setDurum('bekleyen');
                setSayfa(1);
              }}
            >
              {bekleyenSayisi} hesap kişiye bağlanmayı bekliyor
            </Button>
          )}
          {/* ✏️ TUR 9 / B7 (T9-25) — YETKİ DÖKÜMÜ: denetimde istenen belge.
              Yetki = OKUMA yetkisi; bu ekranı gören dökümünü alır. */}
          <Button
            variant="default"
            data-testid="yetki-dokumu"
            loading={indir.isPending}
            onClick={() =>
              indir.mutate({ yol: '/api/app-users/export', yedekAd: 'yetki-dokumu.xlsx' })
            }
          >
            Yetki dökümü (Excel)
          </Button>
        </Group>
      </Group>
      <Text size="sm" c="dimmed">
        Şapka kişinin hesabına verilir; bahçe o şapkayla bakılan yerleri söyler. Bahçesi boş bir
        şapka hiçbir şey göremez.
      </Text>
      <QueryStatus queries={[kullanicilar]} />

      <Group gap="sm" align="flex-end" data-testid="kullanicilar-suzgec">
        <TextInput
          size="sm"
          label="Ara"
          placeholder="Ad veya e-posta"
          data-testid="kullanicilar-arama"
          value={arama}
          onChange={(olay) => {
            setArama(olay.currentTarget.value);
            setSayfa(1);
          }}
        />
        <Select
          searchable
          clearable
          size="sm"
          label="Şapka"
          placeholder="Tümü"
          data-testid="kullanicilar-sapka-suzgeci"
          value={sapkaSuzgeci}
          onChange={(deger) => {
            setSapkaSuzgeci(deger);
            setSayfa(1);
          }}
          data={sapkalarinSecenekleri(hepsi)}
        />
        <Select
          size="sm"
          label="Durum"
          data-testid="kullanicilar-durum-suzgeci"
          value={durum}
          onChange={(deger) => {
            setDurum((deger ?? 'hepsi') as DurumSuzgeci);
            setSayfa(1);
          }}
          data={DURUM_SECENEKLERI}
        />
        <SuzgecGostergesi
          etkinSayisi={etkinSuzgec}
          testId="kullanicilar-suzgec-gostergesi"
          onTemizle={() => {
            setArama('');
            setSapkaSuzgeci(null);
            setDurum('hepsi');
            setSayfa(1);
          }}
        />
      </Group>

      {kullanicilar.isSuccess && suzulmus.length === 0 && (
        <BosListeNotu mesaj="Bu süzgeçle kullanıcı yok." />
      )}

      {sayfadakiler.map((kullanici) => (
        <Card key={kullanici.id} withBorder data-testid="kullanici-karti">
          <Group gap="xs" mb="xs">
            {kullanici.personId ? (
              <Anchor
                component={Link}
                to={`/calisanlar/${kullanici.personId}`}
                fw={600}
                data-testid="kullanici-ad"
              >
                {kullanici.adSoyad ?? kullanici.email ?? 'Adsız hesap'}
              </Anchor>
            ) : (
              <Text fw={600} data-testid="kullanici-ad">
                {kullanici.adSoyad ?? kullanici.email ?? 'Adsız hesap'}
              </Text>
            )}
            <Text size="sm" c="dimmed">
              {kullanici.email ?? '—'}
            </Text>
            {!kullanici.personId && (
              <Rozet anlam="bilgi" testId="kullanici-kisisiz">
                Kişiye bağlı değil
              </Rozet>
            )}
            {kullanici.hicGirmedi && (
              <Rozet anlam="bilgi" testId="kullanici-hic-girmedi">
                Hiç giriş yapmadı
              </Rozet>
            )}
            {!kullanici.aktif && (
              <Rozet anlam="dikkat" testId="kullanici-pasif">
                Pasif
              </Rozet>
            )}
          </Group>
          {/* ✏️ TUR 9 / B3 — bağ kurma/çözme yüzeyi. Yazma yolu
              `calisan-parts.tsx`te TEKTİR (Y1 deseninin bu turdaki kolu). */}
          {bagYazabilir && (
            <HesapBagEylemleri
              appUserId={kullanici.id}
              personId={kullanici.personId}
              kisiSecenekleri={kisiSecenekleri}
            />
          )}
          <SapkaBahceDuzenleyici
            kullanici={kullanici}
            yazabilir={yazabilir}
            testIdOnek="kullanici-yetki"
          />
        </Card>
      ))}

      <Sayfalama
        sayfa={sayfa}
        sayfaBoyutu={SAYFA_BOYUTU}
        toplam={suzulmus.length}
        onDegis={setSayfa}
        testId="kullanicilar-sayfalama"
      />
    </Stack>
  );
}

/** Süzgeç yalnız GERÇEKTEN kullanılan şapkaları gösterir (12 satırlık ölü liste değil). */
function sapkalarinSecenekleri(
  kullanicilar: readonly AppUserSummary[],
): { value: string; label: string }[] {
  const kullanilan = new Set<RoleType>();
  for (const kullanici of kullanicilar) {
    for (const atama of kullanici.atamalar) kullanilan.add(atama.sapka);
  }
  return [...kullanilan]
    .map((sapka) => ({ value: sapka, label: SAPKA_ETIKET[sapka] }))
    .sort((a, b) => a.label.localeCompare(b.label, 'tr'));
}
